import {cloneDeep} from 'lodash';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {IClinicBranch} from './clinic-branch.interface';
import {ClinicState, IClinic} from '../clinic';
import {firstValueFrom} from 'rxjs';

export interface IRClinicBranch {
  clinicBranches: IClinicBranch[];
  count: number;
  selectedBranch: string;
}

export namespace ClinicBranchActions {
  export class Get {
    static readonly type: string = '[ClinicBranch] Get All Clinic branches';
    constructor(public clinicId: string, public filters?: ServiceLoopbackFilters) { }
  }

  export class GetCount {
    static readonly type: string = '[ClinicBranch] Get All Clinic branches Count Action';
    constructor(public clinicId: string) { }
  }

  export class Create {
    static readonly type: string = '[ClinicBranch] Post Clinic branch';
    constructor(public clinicBranch: IClinicBranch, public clinicId: string) { }
  }

  export class Update {
    static readonly type: string = '[ClinicBranch] Patch Clinic branch';
    constructor(public clinicId: string, public clinicBranchId: string, public clinicBranch: Partial<IClinicBranch>) { }
  }

  export class SelectById {
    static readonly type: string = '[ClinicBranch] Select Clinic branch';
    constructor(public clinicBranchId: string) { }
  }

  export class GetById {
    static readonly type: string = '[ClinicBranch] Get Clinic By Id branch';
    constructor(public clinicId: string, public clinicBranchId: string) { }
  }
}


@State<IRClinicBranch>({
  name: 'clinicBranchState',
  defaults: {
    clinicBranches: [],
    count: 0,
    selectedBranch: null,
  },
})

@Injectable()
export class ClinicBranchState {
  constructor(private baseService: BaseService<any>) { }

  url = environment.SERVER_URL;

  @Selector()
  static getClinicBranches(state: IRClinicBranch): IClinicBranch[] {
    return state.clinicBranches;
  }

  @Selector()
  static getClinicBranchesCount(state: IRClinicBranch): number {
    return state.count;
  }

  @Selector([ClinicState.getClinicSelected])
  static getClinicBranchesByClinicSelected(state: IRClinicBranch, clinic: IClinic): IClinicBranch[] {
    return state.clinicBranches.filter((clinicBranch: IClinicBranch) => clinicBranch.clinicId === clinic._id);
  }

  @Selector()
  static getClinicBranchSelected(state: IRClinicBranch): IClinicBranch {
    return cloneDeep(state.clinicBranches.find((clinicBranch: IClinicBranch) => clinicBranch._id === state.selectedBranch));
  }

  @Action(ClinicBranchActions.Get)
  async getAll(
    ctx: StateContext<IRClinicBranch>,
    action?: ClinicBranchActions.Get
  ): Promise<void> {
    if ((!ctx.getState()?.clinicBranches || ctx.getState()?.clinicBranches.length === 0)) {
      const response = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}/branches`, action?.filters)));
      if (response.error) throw new Error(response.message);
      const clinicBranches: IClinicBranch[] = response.entity as IClinicBranch[];
      ctx.patchState({
        clinicBranches,
      });
    }
  }

  @Action(ClinicBranchActions.GetCount)
  async getClinicBranchesCountAction(ctx: StateContext<IRClinicBranch>, action?: ClinicBranchActions.GetCount): Promise<void> {
    const response = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}/branches/count`, void 0)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      count: response.entity.count
    });
  }

  @Action(ClinicBranchActions.Create)
  async postClinicBranch(
    ctx: StateContext<IRClinicBranch>,
    action: ClinicBranchActions.Create
  ): Promise<void> {
    const response = await firstValueFrom(this.baseService.post
      (`${this.url}/clinics/${action.clinicId}/branches`, action.clinicBranch));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      selectedBranch: response.entity._id,
      clinicBranches: [...ctx.getState().clinicBranches, response.entity]
    });
  }

  @Action(ClinicBranchActions.GetById)
  async getClinicBranchByIdAction(ctx: StateContext<IRClinicBranch>, action: ClinicBranchActions.GetById): Promise<void> {
    const response = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}/branches/${action.clinicBranchId}`, void 0)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      selectedBranch: response.entity._id,
      clinicBranches: [response.entity]
    });
  }

  @Action(ClinicBranchActions.Update)
  async patchClinicBranch(
    ctx: StateContext<IRClinicBranch>,
    action: ClinicBranchActions.Update
  ): Promise<void> {
    const response = await firstValueFrom(this.baseService.patch(
      `${this.url}/clinics/${action.clinicId}/branches/${action.clinicBranchId}`, action.clinicBranch));
    if (response.error) throw new Error(response.message);
    const clinicBranches: IClinicBranch[] = cloneDeep(ctx.getState().clinicBranches);
    const clinicBranchIndex = clinicBranches.findIndex(clinicBranch => clinicBranch._id === action.clinicBranchId);
    if (clinicBranchIndex !== -1) {
      clinicBranches[clinicBranchIndex] = { ...clinicBranches[clinicBranchIndex], ...action.clinicBranch };
      ctx.patchState({
        clinicBranches,
        selectedBranch: action.clinicBranchId,
      });
    }
  }

  @Action(ClinicBranchActions.SelectById)
  async selectClinicBranch(ctx: StateContext<IRClinicBranch>, action: ClinicBranchActions.SelectById): Promise<void> {
    ctx.patchState({ selectedBranch: action.clinicBranchId });
  }
}
